import React from 'react';
import { Avatar, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { User } from '@sendbird/chat';
import type { Member } from '@sendbird/chat/groupChannel';
import { pipe, Option } from 'effect';

import type { DirectoryUser } from '../../hooks/useChatUserDirectory';
import { directoryUserName } from '../../hooks/useChatUserDirectory';

interface Props {
  /** The Sendbird user/member for this suggestion row; `userId` is the account GUID. */
  user: User | Member;
  /** GUID → directory user, so the row can show a human name + email instead of the GUID. */
  lookup: ReadonlyMap<string, DirectoryUser>;
}

const RowAvatar = styled(Avatar)({
  width: 28,
  height: 28,
});

/**
 * Custom mention suggestion row content (DA-19769). Rendered via Sendbird's `renderUserMentionItem`
 * hook, it shows the member's name with their email on a secondary line beneath it (two-line
 * layout) and never shows the raw Sendbird user id (a GUID). The email is resolved from the company
 * chat directory by the member's id. The row's hover highlight is provided by the chat skin
 * stylesheet, because Sendbird's custom-render path does not apply its built-in focus/hover state.
 */
const MentionSuggestionItem: React.FC<Props> = ({ user, lookup }) => {
  const directoryUser = lookup.get(user.userId);

  const name = pipe(
    Option.fromNullable(directoryUser),
    Option.map(directoryUserName),
    Option.getOrElse(() => user.nickname || ''),
  );

  const email = pipe(
    Option.fromNullable(directoryUser),
    Option.map((u) => u.userId),
    Option.getOrElse(() => ''),
  );

  const picture = pipe(
    Option.fromNullable(directoryUser),
    Option.map((u) => u.picture),
    Option.getOrElse(() => user.profileUrl || ''),
  );

  return (
    <Stack direction="row" alignItems="center" useFlexGap columnGap={2} flex={1} minWidth={0}>
      <RowAvatar src={picture}>{name.charAt(0).toUpperCase()}</RowAvatar>
      <Stack minWidth={0}>
        <Typography variant="subtitle2" noWrap>
          {name}
        </Typography>
        {email ? (
          <Typography variant="caption" color="text.secondary" noWrap>
            {email}
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
};

export default MentionSuggestionItem;
